'use client'

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:offsoo-landing":true});}catch(e){}}();import { clsx } from 'clsx/lite'

import { AppUiVisual } from '../app-ui-visual'
import {
  client,
  dayMinutes,
  formatHours,
  HOURS_DAY_TARGET,
  HOURS_MONTH,
  HOURS_WEEK_DAYS,
  weekMinutes,
  type HourDay,
  type HourWeek,
} from '../demo-data'
import { useVisualCopy } from '../visual-copy'
import { AppHeader, AppScreen, HeaderStat } from './app-screen'
import styles from './hours-month-visual.module.css'
import { HERO_ENTER_MS, useScenario } from './use-scenario'

/* Saturday and Sunday carry no bookings, so they only made the days that do 40% narrower. */
const WEEKDAYS = HOURS_WEEK_DAYS
const WORKING_DAYS = WEEKDAYS.length

/* The last three weeks: the one being worked, and the two behind it. A month view holds five, but
   at three the cells are large enough to read the bookings in them. The header still reports the
   whole month, as a calendar scrolled to its last weeks does. */
const WEEKS = HOURS_MONTH.slice(-3)

/** The week being booked, and the days of it that land during the loop. */
const LIVE_WEEK = HOURS_MONTH[HOURS_MONTH.length - 1]
const LIVE_DAYS = LIVE_WEEK.days.filter((day) => day.entries.length > 0)

const REST_BEAT = 0 // SSR / reduced-motion snapshot — the week booked, the month at its full total
const EMPTIES_AT = 1 // the running week clears, and the booking starts over
const FIRST_LANDS_AT = 2 // then one day per beat

/** The beat a given day of the live week lands on. */
function landsAt(index: number): number {
  return FIRST_LANDS_AT + index
}

/**
 * How long each beat stays on screen.
 *
 * Twelve beats at a flat 900ms was 10.8 seconds of which 4.5 rendered nothing new: three identical
 * frames of the empty week at one end, four of the booked week at the other, and a beat between
 * every two landings whose only job was to let the previous day's wash fade. The wash fades under
 * the next landing perfectly well.
 *
 * What is left is the week settled, the week clearing, and one beat per day booked. The settled
 * week is held longest because it is the resting frame — the calendar this hero is a picture of —
 * and the clearing is the shortest thing here on purpose: it is the only stroke in the loop that
 * runs the story backwards, and it should be over before it reads as work being deleted.
 */
const BEAT_HOLDS = [1700, 700, ...LIVE_DAYS.map(() => 1150)] as const

/** The contracted day, in minutes. A total past it is what the month view marks. */
const DAY_TARGET_MINUTES = Number(HOURS_DAY_TARGET.split(':')[0]) * 60 + Number(HOURS_DAY_TARGET.split(':')[1])

/**
 * Time-tracking hero — the month of hours, and how one booking travels through it. The calendar is
 * the real one: weeks as rows with their number and total in the gutter, Monday through Sunday as
 * columns, each day headed by its date and the contracted hours for it.
 *
 * The loop books the running week: its days land one at a time, and each one carries straight
 * through — the day's own total appears, the week total in the gutter climbs, and the month total
 * in the header climbs with it. That roll-up is the whole point of keeping hours here rather than
 * in a sheet, and it is the one thing a single booking dialog cannot show. `beat === null` (SSR,
 * no-JS, reduced motion) renders the week booked and the month at its full total.
 *
 * The loop rests on that finished month rather than ending on it: the last landing settles into the
 * resting frame on the wrap, which is also the beat the story starts from again.
 */
export function HoursMonthVisual({ className }: { className?: string }) {
  const { t } = useVisualCopy()
  const { ref, beat } = useScenario(BEAT_HOLDS, { restBeat: REST_BEAT, enterHoldMs: HERO_ENTER_MS })
  const effBeat = beat ?? REST_BEAT

  /** A day of the live week that has not been booked yet, at this beat. Nothing is pending on the
   *  resting beat: that is the finished month, which is what this card is a picture of. */
  const pendingDay = (day: HourDay) => {
    const index = LIVE_DAYS.indexOf(day)
    return index >= 0 && effBeat >= EMPTIES_AT && effBeat < landsAt(index)
  }
  /** The beat a day lands, its own total and everything above it settle. */
  const justLanded = (day: HourDay) => {
    const index = LIVE_DAYS.indexOf(day)
    return index >= 0 && effBeat === landsAt(index)
  }
  const weekJustLanded = LIVE_DAYS.some(justLanded)

  // Totals are summed from the month as it stands at this beat — a day that has not been booked yet
  // contributes nothing — so every figure on the card comes from the same list at the same moment.
  const staged = (day: HourDay): HourDay => (pendingDay(day) ? { ...day, entries: [] } : day)
  const stagedWeek = (week: HourWeek): HourWeek => ({ ...week, days: week.days.map(staged) })
  const monthNow = HOURS_MONTH.reduce((sum, week) => sum + weekMinutes(stagedWeek(week)), 0)

  return (
    <AppUiVisual label={t.screen.hoursMonth.caption} className={className}>
      <AppScreen>
        <div ref={ref} data-visual="hours-month" className={clsx(styles.root, 'flex h-full flex-col')}>
          <AppHeader
            title={t.screen.hoursMonth.title}
            detail={t.period.month}
            right={
              <span className={clsx(styles.total, weekJustLanded && styles.totalLive)}>
                <HeaderStat value={`${formatHours(monthNow)} ${t.word.hoursUnit}`} label={t.word.thisMonth} />
              </span>
            }
          />

          <div className={styles.weekdays}>
            <span />
            {WEEKDAYS.map((day) => (
              <span key={day} className={styles.weekday}>
                {day}
              </span>
            ))}
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {WEEKS.map((week) => {
              const isLive = week === LIVE_WEEK
              return (
                <div key={week.week} className={styles.week}>
                  {/* Week number and week total, in the gutter the app keeps them in. */}
                  <div className={styles.gutter}>
                    <b className={styles.weekNr}>{week.week}</b>
                    <span className={clsx(styles.weekTotal, isLive && weekJustLanded && styles.totalLive)}>
                      {formatHours(weekMinutes(stagedWeek(week)))}
                    </span>
                  </div>

                  {week.days.slice(0, WORKING_DAYS).map((day) => {
                    const minutes = dayMinutes(staged(day))
                    return (
                      <div
                        key={`${week.week}-${day.day}`}
                        className={clsx(
                          styles.day,
                          day.outside && styles.dayOutside,
                          // A cell that just took a booking lights up. In the accent, unless the
                          // booking put the day past its contracted hours — then the mark is the
                          // signal colour, because that is the day worth stopping on.
                          justLanded(day) && (minutes > DAY_TARGET_MINUTES ? styles.dayLandedOver : styles.dayLanded),
                        )}
                      >
                        <div className={styles.dayHead}>
                          <span>{day.day}</span>
                          {minutes > 0 && (
                            <span
                              className={clsx(
                                styles.dayTotal,
                                minutes > DAY_TARGET_MINUTES && styles.totalOver,
                                justLanded(day) && styles.totalLive,
                              )}
                            >
                              {formatHours(minutes)}
                            </span>
                          )}
                        </div>
                        <div className={styles.entries}>
                          {day.entries.map((entry) => (
                            <div
                              key={`${entry.nr}-${entry.hours}`}
                              className={clsx(styles.entry, pendingDay(day) && styles.entryPending)}
                            >
                              <span className={styles.entryName}>{client(entry.nr).short}</span>
                              <span className={styles.entryHours}>{entry.hours}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>

          <div className={styles.footer}>
            <span>
              {t.word.contract} <b>{HOURS_DAY_TARGET}</b> {t.word.perDay}
            </span>
            <span className={styles.footerRight}>
              {t.screen.mobile.week} {LIVE_WEEK.week} · <b>{formatHours(weekMinutes(stagedWeek(LIVE_WEEK)))}</b>{' '}
              {t.word.booked}
            </span>
          </div>
        </div>
      </AppScreen>
    </AppUiVisual>
  )
}
